import type {
  AssetTypeCode,
  CallableFilter,
  MarketFilters,
  MarketFilterTab,
  MarketTab,
  MaturityFilter,
  MaturityMode,
  Range,
  SideFilter,
  SpreadBenchmark,
  DiffToEvalMode,
  DurationType,
  RangeType,
  OidMode,
  TeyFilter,
} from "@/shared/types/bondFilters";
import {
  DEFAULT_MARKET_FILTERS,
  DEFAULT_MATURITY_MODE,
  FILTER_TAB_RESTRICTIONS,
  QUOTE_ONLY_KEYS,
  RFQ_ONLY_KEYS,
  resolveCallableMode,
  resolveMaturityMode,
} from "@/shared/types/bondFilters";
import { isBondTypeCode, isFeatureCode } from "@/shared/types/bondTypes";
import { isBondSectorCode } from "@/shared/types/bondSectors";
import { isBondAttributeCode } from "@/shared/types/bondAttributes";
import { isRateStructureCode } from "@/shared/types/bondRateStructures";
import { isRedemptionStatusCode } from "@/shared/types/bondRedemptionStatuses";
import type { IncludeExcludeFilter } from "@/shared/types/includeExcludeFilter";
import { HISTORY_TRADE_TYPE_OPTIONS, type TradeTypeCode } from "@/shared/types/trade";
import {
  TRADE_DATE_RANGE_OPTIONS,
  TRADE_SIZE_OPTIONS,
  isTradeSizePreset,
  type TradeDateRangePreset,
} from "@/shared/types/tradePresets";

/**
 * URL params that are not market filters. Used to distinguish a "bare" URL
 * (no filter params → restore from localStorage) from a URL that already has
 * user-supplied or router-injected filters. Kept here so both the route's
 * `beforeLoad` and `useMarketFilters` share one source of truth.
 */
export const NON_FILTER_PARAMS = new Set<string>([
  "tab",
  "bond",
  "bondCusip",
  "expanded",
  "watchlist",
  "msg",
  "msgQ",
  "msgPage",
  "msgBucket",
  "msgSort",
  "msgSortDesc",
]);

/** True when the URL carries at least one actual market filter param. */
export function hasFilterSearchParams(sp: URLSearchParams): boolean {
  for (const key of sp.keys()) {
    if (!NON_FILTER_PARAMS.has(key)) return true;
  }
  return false;
}

// ─── Remaining flat scalar keys (not part of a Range<T>/grouped filter) ──────

const STRING_KEYS: (keyof MarketFilters)[] = [
  "moodysMinRating",
  "spMinRating",
  "marketDate",
  "sortBy",
];

const BOOLEAN_KEYS: (keyof MarketFilters)[] = [
  "includeHistorical",
  "showUnknownCusips",
  "ratingsUseUnderlyingOnly",
  "sortDesc",
  // Tax Status — tri-state booleans (true/false/absent).
  "isFederallyTaxable",
  "isStateTaxable",
  "isAmt",
  "isBankQualified",
  // Double-barreled — tri-state boolean (true/false/absent).
  "isDoubleBarreled",
];

/**
 * Enum-valued filter keys. Values are validated against the allowed-set on
 * parse; unknown values are dropped silently to keep the URL well-formed.
 */
const ENUM_KEYS = {
  spreadType: new Set<SpreadBenchmark>(["aaa", "treasury"]),
  diffToEvalMode: new Set<DiffToEvalMode>(["yield", "price"]),
  durationType: new Set<DurationType>(["modified", "effective", "oas"]),
  rangeType: new Set<RangeType>(["convertDate", "tenderPutDate", "refunding"]),
  oidMode: new Set<OidMode>(["yield", "price"]),
  tradeType: new Set<TradeTypeCode>(HISTORY_TRADE_TYPE_OPTIONS.map((option) => option.code)),
  tradeDateRange: new Set<TradeDateRangePreset>(
    TRADE_DATE_RANGE_OPTIONS.map((option) => option.id),
  ),
  tradeSize: new Set<string>(
    TRADE_SIZE_OPTIONS.map((option) => option.id).filter(isTradeSizePreset),
  ),
} as const;

/**
 * Array-valued filter keys encoded as repeated params (`?issuers=a&issuers=b`).
 * Repeated form is safer than CSV because user-entered issuer/obligor names
 * can contain commas.
 */
const ARRAY_KEYS: (keyof MarketFilters)[] = ["cusips", "issuers", "obligors", "states"];

/** Bond Type arrays only accept known taxonomy codes; unknown values (e.g. old bookmarks) are dropped. */
const VALIDATED_BOND_TYPE_ARRAY_KEYS: (keyof MarketFilters)[] = [
  "bondTypesIncluded",
  "bondTypesExcluded",
];

/** Feature arrays only accept known taxonomy codes; unknown values are dropped. */
const VALIDATED_FEATURE_ARRAY_KEYS: (keyof MarketFilters)[] = [
  "featuresIncluded",
  "featuresExcluded",
];

/** Attribute arrays only accept known taxonomy codes; unknown values are dropped. */
const VALIDATED_ATTRIBUTE_ARRAY_KEYS: (keyof MarketFilters)[] = [
  "attributesIncluded",
  "attributesExcluded",
];

const VALID_TABS = new Set<MarketTab>(["quotes", "rfqs", "history", "messages"]);

/** Allowed asset-type codes; unknown values are dropped on parse. */
const VALID_ASSET_TYPES = new Set<AssetTypeCode>([
  "Muni",
  "Corporate",
  "Government",
  "Sovereign",
  "Unknown",
]);

/**
 * Side filter encoding. The default ("Sell"/Offers) is the absence of a `side`
 * param, mirroring how `tab` omits its default. "Full Depth" (undefined) is
 * distinct from the default, so it needs an explicit sentinel value in the URL.
 */
const SIDE_PARAM = "side";
const SIDE_FULL_DEPTH_SENTINEL = "all";
const VALID_SIDES = new Set<SideFilter>(["Buy", "Sell"]);

function applyTabParam(sp: URLSearchParams, filters: MarketFilters): void {
  const tab = sp.get("tab");
  if (tab && VALID_TABS.has(tab as MarketTab)) filters.tab = tab as MarketTab;
}

function appendTabParam(params: URLSearchParams, filters: MarketFilters): void {
  if (filters.tab && filters.tab !== DEFAULT_MARKET_FILTERS.tab) params.set("tab", filters.tab);
}

// ─── Flat scalar key helpers ─────────────────────────────────────────────────

function applyStringKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const rec = filters as unknown as Record<string, unknown>;
  for (const key of STRING_KEYS) {
    const val = sp.get(key);
    if (val) rec[key] = val;
  }
}

function applyBooleanKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const rec = filters as unknown as Record<string, unknown>;
  for (const key of BOOLEAN_KEYS) {
    const val = sp.get(key);
    if (val === "true") rec[key] = true;
    else if (val === "false") rec[key] = false;
  }
}

function applyRatingsCombineFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const ratingsCombine = sp.get("ratingsCombine");
  if (ratingsCombine === "AND" || ratingsCombine === "OR") {
    filters.ratingsCombine = ratingsCombine;
  }

  if (filters.ratingsCombine == null) {
    const legacy = sp.get("ratingsRequireBoth");
    if (legacy === "true") filters.ratingsCombine = "AND";
    else if (legacy === "false") filters.ratingsCombine = "OR";
  }
}

function applyEnumKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const rec = filters as unknown as Record<string, unknown>;
  for (const [key, allowed] of Object.entries(ENUM_KEYS)) {
    let val = sp.get(key);
    if (!val) continue;
    // Legacy bookmark URLs used the misspelled code "aos".
    if (key === "durationType" && val === "aos") val = "oas";
    if ((allowed as Set<string>).has(val)) rec[key] = val;
  }
}

function applyArrayKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const rec = filters as unknown as Record<string, unknown>;
  for (const key of ARRAY_KEYS) {
    const vals = sp.getAll(key).filter((v) => v !== "");
    if (vals.length > 0) rec[key] = vals;
  }

  // Legacy bookmark URLs used `issuer` for the CUSIP6 prefix filter. Fold it
  // into the Advanced Search `issuers` key (same semantics) so old links keep
  // working while only the modern key is ever serialized back.
  const legacyIssuers = sp.getAll("issuer").filter((v) => v !== "");
  if (legacyIssuers.length > 0) {
    filters.issuers = [...new Set([...(filters.issuers ?? []), ...legacyIssuers])];
  }
}

/**
 * Parse the `assetType` param (repeated form: ?assetType=Muni&assetType=Corporate).
 * Absent -> keep the seeded default (["Muni"]). Unknown codes are dropped; if
 * nothing valid remains the seeded default stays.
 */
function applyAssetTypeFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const vals = sp
    .getAll("assetType")
    .filter((v): v is AssetTypeCode => VALID_ASSET_TYPES.has(v as AssetTypeCode));
  if (vals.length > 0) filters.assetType = vals;
}

function applyBondTypeArrayKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const rec = filters as unknown as Record<string, unknown>;
  const keyed: [(keyof MarketFilters)[], (code: string) => boolean][] = [
    [VALIDATED_BOND_TYPE_ARRAY_KEYS, isBondTypeCode],
    [VALIDATED_FEATURE_ARRAY_KEYS, isFeatureCode],
  ];
  for (const [keys, isValid] of keyed) {
    for (const key of keys) {
      const vals = sp.getAll(key).filter((v) => v !== "" && isValid(v));
      if (vals.length > 0) rec[key] = vals;
    }
  }
}

function applySectorArrayKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  // Supports both comma-separated (?sectors=A,B,C) and the legacy repeated-param
  // format (?sectors=A&sectors=B) so existing bookmarks continue to work.
  const vals = sp
    .getAll("sectors")
    .flatMap((v) => v.split(","))
    .filter((v) => v !== "" && isBondSectorCode(v));
  if (vals.length > 0) filters.sectors = vals as MarketFilters["sectors"];
}

function applyAttributeArrayKeysFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const rec = filters as unknown as Record<string, unknown>;
  for (const key of VALIDATED_ATTRIBUTE_ARRAY_KEYS) {
    const vals = sp.getAll(key).filter((v) => v !== "" && isBondAttributeCode(v));
    if (vals.length > 0) rec[key] = vals;
  }
}

/**
 * Parse the `side` param. Encoding:
 *   - absent           -> keep default seeded from DEFAULT_MARKET_FILTERS ("Sell")
 *   - ?side=Buy/Sell     -> set explicitly
 *   - ?side=all (or "") -> Full Depth (undefined)
 * Unknown values are ignored so a malformed URL never breaks the toggle.
 */
function applySideFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  if (!sp.has(SIDE_PARAM)) return;
  const val = sp.get(SIDE_PARAM);
  if (val === SIDE_FULL_DEPTH_SENTINEL || val === "") {
    filters.side = undefined;
  } else if (val && VALID_SIDES.has(val as SideFilter)) {
    filters.side = val as SideFilter;
  }
}

function applyIncludeExcludeFilterFromParams<T extends string>(
  sp: URLSearchParams,
  filters: MarketFilters,
  valuesKey: keyof MarketFilters,
  excludeKey: string,
  validate: (v: string) => v is T,
): void {
  const vals = sp.getAll(valuesKey as string).filter((v): v is T => v !== "" && validate(v));
  if (vals.length === 0) return;
  const exclude = sp.get(excludeKey) === "true";
  (filters as unknown as Record<string, unknown>)[valuesKey as string] = { values: vals, exclude };
}

function appendIncludeExcludeFilter<T extends string>(
  params: URLSearchParams,
  filter: IncludeExcludeFilter<T> | undefined,
  valuesKey: string,
  excludeKey: string,
): void {
  if (!filter || filter.values.length === 0) return;
  for (const val of filter.values) params.append(valuesKey, val);
  if (filter.exclude) params.set(excludeKey, "true");
}

// ─── Nested numeric Range<number> helpers ────────────────────────────────────

/** Numeric `MarketFilters` keys whose value is a `Range<number>`. */
const NUMERIC_RANGE_KEYS: readonly (keyof MarketFilters)[] = [
  "quantity",
  "yieldToWorst",
  "yieldToMaturity",
  "coupon",
  "dollarPrice",
  "spread",
  "diffToEval",
  "duration",
  "oid",
  "maturitySize",
  "dealSize",
];

/** String `MarketFilters` keys whose value is a `Range<string>`. */
const STRING_RANGE_KEYS: readonly (keyof MarketFilters)[] = ["range", "preRefundedDate"];

/**
 * Generic numeric Range<number> parser. Reads `<key>.min` and `<key>.max`
 * params and sets `filters[key]` to the Range object if either bound is
 * present. Malformed values (e.g. `?quantity.min=abc`) must not round-trip as
 * "NaN" — parsed values are validated with `Number.isFinite` before use.
 */
function applyNumericRangeFromParams(
  sp: URLSearchParams,
  filters: MarketFilters,
  key: keyof MarketFilters,
): void {
  const minStr = sp.get(`${key}.min`);
  const maxStr = sp.get(`${key}.max`);
  const minRaw = minStr == null ? undefined : Number.parseFloat(minStr);
  const maxRaw = maxStr == null ? undefined : Number.parseFloat(maxStr);
  const min = minRaw != null && Number.isFinite(minRaw) ? minRaw : undefined;
  const max = maxRaw != null && Number.isFinite(maxRaw) ? maxRaw : undefined;
  if (min != null || max != null) {
    (filters as unknown as Record<string, unknown>)[key] = { min, max } satisfies Range<number>;
  }
}

function appendNumericRange(
  params: URLSearchParams,
  key: string,
  range: Range<number> | undefined,
): void {
  if (!range) return;
  if (range.min != null && Number.isFinite(range.min)) params.set(`${key}.min`, String(range.min));
  if (range.max != null && Number.isFinite(range.max)) params.set(`${key}.max`, String(range.max));
}

/** Generic string Range<string> parser. Reads `<key>.min` and `<key>.max`. */
function applyStringRangeFromParams(
  sp: URLSearchParams,
  filters: MarketFilters,
  key: keyof MarketFilters,
): void {
  const min = sp.get(`${key}.min`) ?? undefined;
  const max = sp.get(`${key}.max`) ?? undefined;
  if (min || max) {
    (filters as unknown as Record<string, unknown>)[key] = { min, max } satisfies Range<string>;
  }
}

function appendStringRange(
  params: URLSearchParams,
  key: string,
  range: Range<string> | undefined,
): void {
  if (!range) return;
  if (range.min) params.set(`${key}.min`, range.min);
  if (range.max) params.set(`${key}.max`, range.max);
}

// ─── Nested group handlers ────────────────────────────────────────────────────

const VALID_MATURITY_MODES = new Set<MaturityMode>(["year", "date", "years", "days"]);

function parsedNum(str: string | null): number | undefined {
  if (str == null) return undefined;
  const n = Number.parseFloat(str);
  return Number.isFinite(n) ? n : undefined;
}

function applyMaturityFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const modeStr = sp.get("maturity.mode");
  const mode =
    modeStr && VALID_MATURITY_MODES.has(modeStr as MaturityMode)
      ? (modeStr as MaturityMode)
      : undefined;

  const yearMin = parsedNum(sp.get("maturity.year.min"));
  const yearMax = parsedNum(sp.get("maturity.year.max"));
  const dateMin = sp.get("maturity.date.min") ?? undefined;
  const dateMax = sp.get("maturity.date.max") ?? undefined;
  const yearsOutMin = parsedNum(sp.get("maturity.yearsOut.min"));
  const yearsOutMax = parsedNum(sp.get("maturity.yearsOut.max"));
  const daysMin = parsedNum(sp.get("maturity.days.min"));
  const daysMax = parsedNum(sp.get("maturity.days.max"));

  const hasAny =
    mode != null ||
    yearMin != null ||
    yearMax != null ||
    dateMin ||
    dateMax ||
    yearsOutMin != null ||
    yearsOutMax != null ||
    daysMin != null ||
    daysMax != null;
  if (!hasAny) return;

  const maturity: MaturityFilter = { mode };
  if (yearMin != null || yearMax != null) maturity.year = { min: yearMin, max: yearMax };
  if (dateMin || dateMax) maturity.date = { min: dateMin, max: dateMax };
  if (yearsOutMin != null || yearsOutMax != null)
    maturity.yearsOut = { min: yearsOutMin, max: yearsOutMax };
  if (daysMin != null || daysMax != null) maturity.days = { min: daysMin, max: daysMax };
  filters.maturity = maturity;
}

function appendMaturity(params: URLSearchParams, filters: MarketFilters): void {
  const m = filters.maturity;
  if (!m) return;
  const mode = resolveMaturityMode(filters);
  if (mode !== DEFAULT_MATURITY_MODE) params.set("maturity.mode", mode);
  appendNumericRange(params, "maturity.year", m.year);
  appendStringRange(params, "maturity.date", m.date);
  appendNumericRange(params, "maturity.yearsOut", m.yearsOut);
  appendNumericRange(params, "maturity.days", m.days);
}

function applyCallableFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const modeRaw = sp.get("callable.mode");
  const mode =
    modeRaw === "year" || modeRaw === "date" || modeRaw === "years" || modeRaw === "days"
      ? modeRaw
      : undefined;
  const yearMin = parsedNum(sp.get("callable.year.min"));
  const yearMax = parsedNum(sp.get("callable.year.max"));
  const nextCallDateMin = sp.get("callable.nextCallDate.min") ?? undefined;
  const nextCallDateMax = sp.get("callable.nextCallDate.max") ?? undefined;
  const yearsOutMin = parsedNum(sp.get("callable.yearsOut.min"));
  const yearsOutMax = parsedNum(sp.get("callable.yearsOut.max"));
  const daysMin = parsedNum(sp.get("callable.days.min"));
  const daysMax = parsedNum(sp.get("callable.days.max"));
  const useFullDateStr = sp.get("callable.useFullDate");
  let useFullDate: boolean | undefined;
  if (useFullDateStr === "true") useFullDate = true;
  else if (useFullDateStr === "false") useFullDate = false;
  const excludeExtraordinary =
    sp.get("callable.excludeExtraordinary") === "true" ? true : undefined;

  const hasAny =
    mode != null ||
    yearMin != null ||
    yearMax != null ||
    nextCallDateMin ||
    nextCallDateMax ||
    yearsOutMin != null ||
    yearsOutMax != null ||
    daysMin != null ||
    daysMax != null ||
    useFullDate != null ||
    excludeExtraordinary != null;
  if (!hasAny) return;

  const callable: CallableFilter = { mode, useFullDate, excludeExtraordinary };
  if (yearMin != null || yearMax != null) callable.year = { min: yearMin, max: yearMax };
  if (nextCallDateMin || nextCallDateMax) {
    callable.nextCallDate = { min: nextCallDateMin, max: nextCallDateMax };
  }
  if (yearsOutMin != null || yearsOutMax != null) {
    callable.yearsOut = { min: yearsOutMin, max: yearsOutMax };
  }
  if (daysMin != null || daysMax != null) callable.days = { min: daysMin, max: daysMax };
  filters.callable = callable;
}

function appendCallable(params: URLSearchParams, filters: MarketFilters): void {
  const c = filters.callable;
  if (!c) return;
  const mode = resolveCallableMode(filters);
  if (mode !== DEFAULT_MATURITY_MODE) params.set("callable.mode", mode);
  appendNumericRange(params, "callable.year", c.year);
  appendStringRange(params, "callable.nextCallDate", c.nextCallDate);
  appendNumericRange(params, "callable.yearsOut", c.yearsOut);
  appendNumericRange(params, "callable.days", c.days);
  // Legacy flag for older clients / bookmarks.
  if (c.useFullDate != null) params.set("callable.useFullDate", String(c.useFullDate));
  if (c.excludeExtraordinary) params.set("callable.excludeExtraordinary", "true");
}

function applyTeyFromParams(sp: URLSearchParams, filters: MarketFilters): void {
  const federalRate = parsedNum(sp.get("tey.federalRate"));
  const stateRate = parsedNum(sp.get("tey.stateRate"));
  const userState = sp.get("tey.userState") ?? undefined;
  const rangeMin = parsedNum(sp.get("tey.min"));
  const rangeMax = parsedNum(sp.get("tey.max"));

  if (
    federalRate == null &&
    stateRate == null &&
    !userState &&
    rangeMin == null &&
    rangeMax == null
  )
    return;

  const tey: TeyFilter = { federalRate, stateRate, userState };
  if (rangeMin != null || rangeMax != null) tey.range = { min: rangeMin, max: rangeMax };
  filters.tey = tey;
}

function appendTey(params: URLSearchParams, filters: MarketFilters): void {
  const t = filters.tey;
  if (!t) return;
  if (t.federalRate != null) params.set("tey.federalRate", String(t.federalRate));
  if (t.stateRate != null) params.set("tey.stateRate", String(t.stateRate));
  if (t.userState) params.set("tey.userState", t.userState);
  if (t.range?.min != null) params.set("tey.min", String(t.range.min));
  if (t.range?.max != null) params.set("tey.max", String(t.range.max));
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function parseFiltersFromSearchParams(sp: URLSearchParams): MarketFilters {
  const filters: MarketFilters = { ...DEFAULT_MARKET_FILTERS };

  applyTabParam(sp, filters);

  // Nested numeric/string ranges
  for (const key of NUMERIC_RANGE_KEYS) applyNumericRangeFromParams(sp, filters, key);
  for (const key of STRING_RANGE_KEYS) applyStringRangeFromParams(sp, filters, key);

  // Complex grouped filters
  applyMaturityFromParams(sp, filters);
  applyCallableFromParams(sp, filters);
  applyTeyFromParams(sp, filters);

  // Remaining flat scalars
  applyStringKeysFromParams(sp, filters);
  applyBooleanKeysFromParams(sp, filters);
  applyRatingsCombineFromParams(sp, filters);
  applyEnumKeysFromParams(sp, filters);
  applyArrayKeysFromParams(sp, filters);
  applyBondTypeArrayKeysFromParams(sp, filters);
  applySectorArrayKeysFromParams(sp, filters);
  applyAttributeArrayKeysFromParams(sp, filters);
  applyAssetTypeFromParams(sp, filters);
  applySideFromParams(sp, filters);
  applyIncludeExcludeFilterFromParams(
    sp,
    filters,
    "rateStructures",
    "rateStructuresExclude",
    isRateStructureCode,
  );
  applyIncludeExcludeFilterFromParams(
    sp,
    filters,
    "redemptionStatuses",
    "redemptionStatusesExclude",
    isRedemptionStatusCode,
  );

  return filters;
}

/**
 * Serialize the `side` filter. Mirrors applySideFromParams:
 *   - default ("Sell")  -> omit
 *   - "Buy"             -> ?side=Buy
 *   - undefined         -> ?side=all (Full Depth must be persisted explicitly
 *                          so it survives a re-parse, otherwise the default
 *                          would clobber it back to "Sell").
 */
function sideAppliesToTab(tab: MarketTab): boolean {
  const allowed = FILTER_TAB_RESTRICTIONS.side;
  return allowed == null || allowed.includes(tab as MarketFilterTab);
}

function appendSideToParams(params: URLSearchParams, filters: MarketFilters): void {
  // Side is Quotes-only; on RFQs/History a masked-out (undefined) side must not
  // be serialized as the Full Depth sentinel.
  if (!sideAppliesToTab(filters.tab)) return;
  if (filters.side === DEFAULT_MARKET_FILTERS.side) return;
  if (filters.side === undefined) {
    params.set(SIDE_PARAM, SIDE_FULL_DEPTH_SENTINEL);
  } else {
    params.set(SIDE_PARAM, filters.side);
  }
}

function appendStringKeys(params: URLSearchParams, filters: MarketFilters): void {
  for (const key of STRING_KEYS) {
    const val = filters[key];
    if (typeof val === "string" && val !== "") params.set(key, val);
  }
}

function appendBooleanKeys(params: URLSearchParams, filters: MarketFilters): void {
  for (const key of BOOLEAN_KEYS) {
    const val = filters[key];
    if (typeof val === "boolean") params.set(key, String(val));
  }
}

function appendRatingsCombine(params: URLSearchParams, filters: MarketFilters): void {
  if (filters.ratingsCombine != null) params.set("ratingsCombine", filters.ratingsCombine);
}

function appendEnumKeys(params: URLSearchParams, filters: MarketFilters): void {
  for (const key of Object.keys(ENUM_KEYS)) {
    const val = (filters as unknown as Record<string, unknown>)[key];
    if (typeof val === "string" && val !== "") params.set(key, val);
  }
}

function appendStringArrayKeys(params: URLSearchParams, filters: MarketFilters): void {
  for (const key of ARRAY_KEYS) {
    const val = filters[key];
    if (!Array.isArray(val)) continue;
    for (const item of val) {
      if (typeof item === "string" && item !== "") params.append(key, item);
    }
  }
}

/**
 * Serialize `assetType` as repeated params, omitting the default (["Muni"])
 * the same way `tab` and `side` omit theirs. Mirrors applyAssetTypeFromParams.
 */
function appendAssetTypeParam(params: URLSearchParams, filters: MarketFilters): void {
  const assetType = filters.assetType;
  if (!Array.isArray(assetType) || assetType.length === 0) return;
  const valid = assetType.filter((v) => VALID_ASSET_TYPES.has(v));
  if (valid.length === 0) return;
  const defaults = DEFAULT_MARKET_FILTERS.assetType ?? [];
  const isDefault = valid.length === defaults.length && valid.every((v, i) => v === defaults[i]);
  if (isDefault) return;
  for (const val of valid) params.append("assetType", val);
}

/** Serialize sectors as a single comma-separated param (?sectors=A,B,C). */
function appendSectorParam(params: URLSearchParams, filters: MarketFilters): void {
  const sectors = filters.sectors;
  if (!Array.isArray(sectors) || sectors.length === 0) return;
  const valid = sectors.filter((s) => isBondSectorCode(s));
  if (valid.length > 0) params.set("sectors", valid.join(","));
}

/**
 * Append repeated `?key=value` params for each validated taxonomy-coded array
 * (bond types, attributes). Unknown codes are silently dropped so
 * the URL stays well-formed even after taxonomy churn.
 */
function appendValidatedArrayKeys(
  params: URLSearchParams,
  filters: MarketFilters,
  keys: readonly (keyof MarketFilters)[],
  validate: (code: string) => boolean,
): void {
  for (const key of keys) {
    const val = filters[key];
    if (!Array.isArray(val)) continue;
    for (const item of val) {
      if (typeof item === "string" && item !== "" && validate(item)) {
        params.append(key, item);
      }
    }
  }
}

export function filtersToSearchParams(filters: MarketFilters): URLSearchParams {
  const params = new URLSearchParams();

  appendTabParam(params, filters);

  // Nested numeric/string ranges
  for (const key of NUMERIC_RANGE_KEYS) {
    appendNumericRange(params, key, filters[key] as Range<number> | undefined);
  }
  for (const key of STRING_RANGE_KEYS) {
    appendStringRange(params, key, filters[key] as Range<string> | undefined);
  }

  // Complex grouped filters
  appendMaturity(params, filters);
  appendCallable(params, filters);
  appendTey(params, filters);

  // Remaining flat scalars
  appendStringKeys(params, filters);
  appendBooleanKeys(params, filters);
  appendRatingsCombine(params, filters);
  appendEnumKeys(params, filters);
  appendStringArrayKeys(params, filters);
  appendValidatedArrayKeys(params, filters, VALIDATED_BOND_TYPE_ARRAY_KEYS, isBondTypeCode);
  appendValidatedArrayKeys(params, filters, VALIDATED_FEATURE_ARRAY_KEYS, isFeatureCode);
  appendSectorParam(params, filters);
  appendValidatedArrayKeys(params, filters, VALIDATED_ATTRIBUTE_ARRAY_KEYS, isBondAttributeCode);
  appendAssetTypeParam(params, filters);
  appendSideToParams(params, filters);
  appendIncludeExcludeFilter(
    params,
    filters.rateStructures,
    "rateStructures",
    "rateStructuresExclude",
  );
  appendIncludeExcludeFilter(
    params,
    filters.redemptionStatuses,
    "redemptionStatuses",
    "redemptionStatusesExclude",
  );

  return params;
}

/** Remove tab-specific filter keys when switching between Quotes and RFQs. */
export function clearStaleTabFilters(filters: MarketFilters, newTab: MarketTab): MarketFilters {
  const cleaned = { ...filters, tab: newTab, sortBy: undefined, sortDesc: undefined };

  const keysToRemove = newTab === "quotes" ? RFQ_ONLY_KEYS : QUOTE_ONLY_KEYS;
  for (const key of keysToRemove) {
    delete (cleaned as unknown as Record<string, unknown>)[key];
  }

  return cleaned;
}
